// External Dependencies
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Helmet } from "react-helmet"
import { notification, Modal } from "antd"
import moment from "moment"

// Internal Components
import { ContentWrapper } from "components"
import {
	RouteDetailPanel,
	RoutesTable,
	MultiRoutePanel,
	MoveOrdersTimeline,
	RouteSequenceMap,
	RevertOptimizationModal,
} from "./components"

// Types
import { Route, RouteStatus, Grid, Vehicle, NotDeliveredReason } from "./types"
import { MovePreviewState } from "./components/MoveOrdersTimeline/types"
import { isRouteEligibleForMoveOrders } from "./components/MoveOrdersTimeline/utils"

// Hooks
import { useBreadcrumbFilters, ALL_CONTRACTS_VALUE } from "hooks"

// Store & State Management
import {
	setLoader,
	setBreadcrumb,
	delayedDispatch,
	setRoutesTableWidth,
	setRoutesPageSize,
} from "store/actions"

// Utilities
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"

// Styles
import "./styles.scss"

const isDev =
	process.env.ENVIRONMENT === undefined ||
	process.env.ENVIRONMENT === null ||
	process.env.ENVIRONMENT === "local" ||
	process.env.ENVIRONMENT === "dev"

const Routes: React.FC = () => {
	const dispatch = useDispatch()

	// Use global breadcrumb filters hook
	const { breadcrumbActions, deleteModal } = useBreadcrumbFilters({
		autoSelectFirst: false,
		onDeleteModalSuccess: () => fetchData(),
		allowAllContracts: true,
	})

	const [revertModalVisible, setRevertModalVisible] = useState(false)

	// Redux variables - memoize to prevent unnecessary re-renders
	const rawFilters = useSelector((state: any) => state.filters) || {}
	const filters = useMemo(
		() => ({
			business: rawFilters.business,
			contract: rawFilters.contract,
			date: rawFilters.date,
		}),
		[rawFilters.business, rawFilters.contract, rawFilters.date]
	)

	// Get table width and page size from Redux
	const tableWidth = useSelector((state: any) => state.routesTableWidth) || 70
	const routesPageSize = useSelector((state: any) => state.routesPageSize)

	// State management
	const [routes, setRoutes] = useState<Route[]>([])
	const [grids, setGrids] = useState<Grid[]>([])
	const [vehicles, setVehicles] = useState<Vehicle[]>([])
	const [drivers, setDrivers] = useState<any[]>([])
	const [notDeliveredReasons, setNotDeliveredReasons] = useState<
		NotDeliveredReason[]
	>([])
	const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([])
	const [showPanel, setShowPanel] = useState(true)
	const [showTimeline, setShowTimeline] = useState(false)
	const [movePreview, setMovePreview] = useState<MovePreviewState>({
		stopsByRoute: {},
		sequenceByRoute: {},
		pendingMoves: new Map(),
		positionOverrides: {},
		selectedOrderId: null,
	})
	const [externalDeliveryActive, setExternalDeliveryActive] = useState(false)
	const [selectedStatus, setSelectedStatus] = useState<RouteStatus[]>([])
	const [searchTerm, setSearchTerm] = useState<string>("")
	const [pagination, setPagination] = useState({
		page: 0,
		pageSize: routesPageSize || 50,
		total: 0,
	})

	// Track if we're currently fetching to prevent infinite loops
	const isFetchingRef = useRef(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const isDraggingRef = useRef(false)

	const fetchData = useCallback(async () => {
		isFetchingRef.current = true

		try {
			await fetchRoutes({
				dispatch,
				filters,
				selectedStatus,
				searchTerm,
				page: pagination.page,
				pageSize: pagination.pageSize,
				setRoutes,
				setPagination,
				setExternalDeliveryActive,
			})
		} finally {
			isFetchingRef.current = false
		}
	}, [
		dispatch,
		filters,
		selectedStatus,
		searchTerm,
		pagination.page,
		pagination.pageSize,
	])

	// Clear grid, vehicle, driver, contract details, and notDeliveredReasons state when contract or business changes
	useEffect(() => {
		setGrids([])
		setVehicles([])
		setDrivers([])
		setNotDeliveredReasons([])
		setSelectedRouteIds([])
	}, [filters.contract, filters.business])

	// The timeline needs at least 1 selected route — if the selection drops
	// to none while it's open, fall back to the side panel instead of
	// leaving both hidden (showPanel was turned off when the timeline was
	// opened, since only one of the two is ever shown at a time).
	useEffect(() => {
		if (selectedRouteIds.length < 1 && showTimeline) {
			setShowTimeline(false)
			setShowPanel(true)
		}
	}, [selectedRouteIds.length, showTimeline])

	// Set the page size in Redux whenever it changes in local state
	useEffect(() => {
		dispatch(setRoutesPageSize(pagination.pageSize))
	}, [dispatch, pagination.page, pagination.pageSize])

	// Fetch routes when filters change
	useEffect(() => {
		// Prevent infinite loop
		if (isFetchingRef.current) {
			return
		}

		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		filters.business,
		filters.contract,
		filters.date,
		selectedStatus,
		searchTerm,
		filters,
		pagination.page,
		pagination.pageSize,
	])

	// Fetch grids, vehicles, drivers, contract details, and notDeliveredReasons when route panel is opened to make first load faster (Data is saved after first fetch to avoid re-fetching)
	useEffect(() => {
		if (selectedRouteIds.length === 0) return

		// If none needs to be fetched, return early
		const needToFetchGrids = grids.length === 0
		const needToFetchVehicles = vehicles.length === 0
		const needToFetchDrivers = drivers.length === 0
		const needToFetchNotDeliveredReasons = notDeliveredReasons.length === 0
		if (
			!needToFetchGrids &&
			!needToFetchVehicles &&
			!needToFetchDrivers &&
			!needToFetchNotDeliveredReasons
		) {
			return
		}

		try {
			// Show loader
			dispatch(setLoader(true))

			// Grid/vehicle/driver are scoped to a single contract, so "all
			// contracts" is treated the same as no contract selected — these
			// functions already clear their state when contract is falsy.
			const contractForScopedFetches =
				filters.contract === ALL_CONTRACTS_VALUE ? undefined : filters.contract

			// Fetch grids if not already fetched
			if (grids.length === 0) {
				fetchGrids({
					contract: contractForScopedFetches,
					setGrids,
					currentGrids: grids,
				})
			}

			// Fetch vehicles if not already fetched
			if (vehicles.length === 0) {
				fetchVehicles({
					contract: contractForScopedFetches,
					setVehicles,
					currentVehicles: vehicles,
				})
			}

			// Fetch drivers if not already fetched
			if (drivers.length === 0) {
				fetchDrivers({
					contract: contractForScopedFetches,
					setDrivers,
					currentDrivers: drivers,
				})
			}

			// Fetch not delivered reasons if not already fetched
			if (notDeliveredReasons.length === 0) {
				fetchNotDeliveredReasons({
					business: filters.business,
					setNotDeliveredReasons,
				})
			}
		} finally {
			// Hide loader
			dispatch(setLoader(false))
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, filters.business, filters.contract, selectedRouteIds.length])

	// Fetch notDeliveredReasons
	useEffect(() => {
		const needToFetchNotDeliveredReasons = notDeliveredReasons.length === 0
		if (!needToFetchNotDeliveredReasons) {
			return
		}

		try {
			// Fetch not delivered reasons if not already fetched
			if (notDeliveredReasons.length === 0) {
				fetchNotDeliveredReasons({
					business: filters.business,
					setNotDeliveredReasons,
				})
			}
		} finally {
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, filters.business])

	// Event handlers
	const handleStatusChange = useCallback((status: RouteStatus[]) => {
		setSelectedStatus(status)
	}, [])

	const handleStatusBlur = useCallback(() => {
		// Status change already triggers refetch via useEffect
	}, [])

	const handleStatusClear = useCallback(() => {
		setSelectedStatus([])
	}, [])

	const handleSearch = useCallback((value: string) => {
		setSearchTerm(value)
		// Reset to first page when searching
		setPagination((prev) => ({ ...prev, page: 0 }))
	}, [])

	const handlePageChange = useCallback((page: number, pageSize: number) => {
		setPagination((prev) => ({ ...prev, page, pageSize }))
	}, [])

	// The side panel and the move-orders timeline can't both fit on screen
	// without one crowding out the other (the map panel's height varies with
	// its content, so the row above the timeline never lines up cleanly) —
	// so only one is ever shown at a time.
	const handleTogglePanel = useCallback(() => {
		setShowPanel((prev) => {
			const next = !prev
			if (next) setShowTimeline(false)
			return next
		})
	}, [])

	const handleToggleTimeline = useCallback(() => {
		if (showTimeline) {
			setShowTimeline(false)
			return
		}

		const openTimeline = () => {
			setShowTimeline(true)
			setShowPanel(false)
		}

		// A route already on_transit is still allowed in the timeline (the
		// driver may already be out with it), but that's serious enough to
		// require an explicit confirmation before letting the user touch it.
		const inTransitRoutes = routes.filter(
			(route) =>
				selectedRouteIds.includes(route._id) && route.status === "on_transit"
		)
		if (inTransitRoutes.length === 0) {
			openTimeline()
			return
		}

		Modal.confirm({
			title: Strings.routes.moveOrdersInTransitWarningTitle,
			content: Strings.formatString(
				Strings.routes.moveOrdersInTransitWarningContent,
				inTransitRoutes.map((route) => route.name).join(", ")
			),
			okText: Strings.routes.moveOrdersInTransitWarningConfirm,
			cancelText: Strings.routes.moveOrdersOptimizeCancel,
			okButtonProps: { danger: true },
			onOk: openTimeline,
		})
	}, [showTimeline, routes, selectedRouteIds])

	const handlePreviewChange = useCallback((state: MovePreviewState) => {
		setMovePreview(state)
	}, [])

	const handleRowPress = useCallback((row: any, event?: React.MouseEvent) => {
		const routeId = row.original?._id || row._id

		// Check if Ctrl or Cmd key is pressed for multi-selection
		if (event && (event.ctrlKey || event.metaKey)) {
			setSelectedRouteIds((prev) => {
				if (prev.includes(routeId)) {
					// Deselect if already selected
					return prev.filter((id) => id !== routeId)
				} else {
					// Add to selection
					return [...prev, routeId]
				}
			})
		} else {
			// Single selection (replace all)
			setSelectedRouteIds([routeId])
		}
	}, [])

	const handlePanelClose = useCallback(() => {
		setSelectedRouteIds([])
	}, [])

	const handleSelectAll = useCallback(() => {
		setSelectedRouteIds(routes.map((route) => route._id))
	}, [routes])

	// Handle resize drag
	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault()
		isDraggingRef.current = true
		document.body.style.cursor = "col-resize"
		document.body.style.userSelect = "none"
	}, [])

	// Handle double-click to reset to default width
	const handleDoubleClick = useCallback(() => {
		dispatch(setRoutesTableWidth(70))
	}, [dispatch])

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isDraggingRef.current || !containerRef.current) return

			const containerRect = containerRef.current.getBoundingClientRect()
			const newWidth =
				((e.clientX - containerRect.left) / containerRect.width) * 100

			// Constrain width between 30% and 85%
			const clampedWidth = Math.min(Math.max(newWidth, 30), 85)
			dispatch(setRoutesTableWidth(clampedWidth))
		}

		const handleMouseUp = () => {
			if (isDraggingRef.current) {
				isDraggingRef.current = false
				document.body.style.cursor = ""
				document.body.style.userSelect = ""
			}
		}

		document.addEventListener("mousemove", handleMouseMove)
		document.addEventListener("mouseup", handleMouseUp)

		return () => {
			document.removeEventListener("mousemove", handleMouseMove)
			document.removeEventListener("mouseup", handleMouseUp)
		}
	}, [dispatch])

	// Dev-only: revert every route of one optimizer call
	const revertOptimizationAction = useMemo(
		() => ({
			type: "button",
			text: "↺ Revert optimization",
			className: "BreadcrumbRevertButton",
			onClick: () => setRevertModalVisible(true),
			disabled:
				!filters.business ||
				!filters.contract ||
				filters.contract === ALL_CONTRACTS_VALUE ||
				!filters.date ||
				pagination.total === 0,
			margin: "right",
		}),
		[filters.business, filters.contract, filters.date, pagination.total]
	)

	// Setup breadcrumb with filters
	useEffect(() => {
		delayedDispatch(
			setBreadcrumb(() => ({
				locations: [
					{
						text: Strings.sidebar.routes,
						icon: "route",
					},
				],
				actions: isDev
					? [revertOptimizationAction, ...breadcrumbActions]
					: breadcrumbActions,
			}))
		)
	}, [breadcrumbActions, revertOptimizationAction])

	const panelVisible = selectedRouteIds.length > 0 && showPanel
	const selectedRoutesForTimeline = useMemo(
		() => routes.filter((route) => selectedRouteIds.includes(route._id)),
		[routes, selectedRouteIds]
	)
	const timelineVisible =
		selectedRouteIds.length >= 1 &&
		showTimeline &&
		selectedRoutesForTimeline.every(isRouteEligibleForMoveOrders)

	return (
		<>
			<Helmet>
				<title>{Strings.sidebar.routes}</title>
				<meta name="description" content="Manage and view all routes" />
			</Helmet>
			<ContentWrapper extraClass="RoutesScreenWrapper">
				<div className="RoutesScreen" ref={containerRef}>
					<div
						className={`RoutesScreen__table ${panelVisible ? "RoutesScreen__table--with-panel" : ""}`}
						style={panelVisible ? { flex: `0 0 ${tableWidth}%` } : undefined}
					>
						<RoutesTable
							routes={routes}
							handleSearch={handleSearch}
							selectedRouteIds={selectedRouteIds}
							setSelectedRouteIds={setSelectedRouteIds}
							handleRowPress={handleRowPress}
							selectedStatus={selectedStatus}
							handleStatusChange={handleStatusChange}
							handleStatusBlur={handleStatusBlur}
							handleStatusClear={handleStatusClear}
							fetchData={fetchData}
							pagination={pagination}
							onPageChange={handlePageChange}
							notDeliveredReasons={notDeliveredReasons}
							externalDeliveryActive={externalDeliveryActive}
							showPanel={showPanel}
							onTogglePanel={handleTogglePanel}
							showTimeline={showTimeline}
							onToggleTimeline={handleToggleTimeline}
						/>
					</div>
					{(panelVisible || timelineVisible) && (
						<>
							<div
								className="RoutesScreen__resizer"
								onMouseDown={handleMouseDown}
								onDoubleClick={handleDoubleClick}
							/>
							<div
								className={`RoutesScreen__panel ${timelineVisible ? "RoutesScreen__panel--map" : ""}`}
								style={{ flex: `0 0 ${100 - tableWidth}%` }}
							>
								{timelineVisible ? (
									<RouteSequenceMap
										selectedRoutes={selectedRoutesForTimeline}
										movePreview={movePreview}
									/>
								) : selectedRouteIds.length === 1 ? (
									<RouteDetailPanel
										routeId={selectedRouteIds[0]}
										routes={routes}
										grids={grids}
										vehicles={vehicles}
										drivers={drivers}
										setRoutes={setRoutes}
										onClose={handlePanelClose}
										notDeliveredReasons={notDeliveredReasons}
									/>
								) : (
									<MultiRoutePanel
										selectedRoutes={routes.filter((route) =>
											selectedRouteIds.includes(route._id)
										)}
										totalRoutes={routes.length}
										onSelectAll={handleSelectAll}
										setRoutes={setRoutes}
										onClose={handlePanelClose}
									/>
								)}
							</div>
						</>
					)}
				</div>
				{timelineVisible && (
					<MoveOrdersTimeline
						selectedRoutes={selectedRoutesForTimeline}
						allRoutes={routes}
						onSuccess={fetchData}
						onPreviewChange={handlePreviewChange}
					/>
				)}
				{deleteModal}
				{isDev && (
					<RevertOptimizationModal
						visible={revertModalVisible}
						onClose={() => setRevertModalVisible(false)}
						onReverted={fetchData}
						business={filters.business}
						contract={filters.contract}
						date={filters.date ? moment.utc(filters.date) : null}
					/>
				)}
			</ContentWrapper>
		</>
	)
}

async function fetchRoutes({
	dispatch,
	filters,
	selectedStatus,
	searchTerm,
	page,
	pageSize,
	setRoutes,
	setPagination,
	setExternalDeliveryActive,
}: {
	dispatch: (action: any) => void
	filters: {
		business?: any
		contract?: any
		date?: any
		[key: string]: any
	}
	selectedStatus: RouteStatus[]
	searchTerm: string
	page: number
	pageSize: number
	setRoutes: React.Dispatch<React.SetStateAction<Route[]>>
	setPagination: React.Dispatch<
		React.SetStateAction<{ page: number; pageSize: number; total: number }>
	>
	setExternalDeliveryActive: React.Dispatch<React.SetStateAction<boolean>>
}) {
	// Only fetch if we have business, contract, and date
	if (!filters.business || !filters.contract || !filters.date) {
		setRoutes([])
		setPagination({ page: 0, pageSize, total: 0 })
		setExternalDeliveryActive(false)
		return
	}

	dispatch(setLoader(true))

	try {
		const url = Endpoints.v2.uriRoutes({
			businessId: filters.business,
			// Omitting contractId (rather than sending "all") makes the API
			// return routes across every contract for this business.
			contractId:
				filters.contract === ALL_CONTRACTS_VALUE ? undefined : filters.contract,
			status: selectedStatus.length > 0 ? selectedStatus : undefined,
			date: new Date(filters.date).toISOString().split("T")[0],
			page,
			pageSize,
			search: searchTerm || undefined,
		})
		const response = await API.get({ url })

		const routes = response.data?.results?.routes ?? []
		const total = response.data?.results?.totalRoutes ?? routes.length

		setRoutes(routes)
		setPagination({ page, pageSize, total })
		setExternalDeliveryActive(
			response.data?.results?.externalDeliveryActive ?? false
		)
	} catch (error) {
		console.error("Error fetching routes:", error)
		setRoutes([])
		setPagination({ page: 0, pageSize, total: 0 })
		setExternalDeliveryActive(false)
	} finally {
		dispatch(setLoader(false))
	}
}

async function fetchGrids({
	contract,
	currentGrids,
	setGrids,
}: {
	contract?: string
	currentGrids: any[]
	setGrids: React.Dispatch<React.SetStateAction<any[]>>
}) {
	// Only fetch if we have contract
	if (!contract) {
		// Only clear vehicles if they're not already empty to prevent unnecessary state updates
		if (currentGrids.length > 0) {
			setGrids([])
		}
		return
	}

	try {
		const url = Endpoints.uriGrids()
		const response = await API.get({ url })

		// Handle error response
		if (!response.ok) {
			console.error("Failed to fetch grids:", response.data)
			notification.error({
				message: "Error",
				description: "Failed to fetch grids.",
			})
		}

		// Set grids
		const { grids = [] } = response.data.results || {}
		setGrids(grids.filter((grid: Grid) => !grid.disabled))
	} catch (err) {
		console.error("Failed to fetch grids:", err)
	}
}

async function fetchVehicles({
	contract,
	currentVehicles,
	setVehicles,
}: {
	contract?: string
	currentVehicles: Vehicle[]
	setVehicles: React.Dispatch<React.SetStateAction<Vehicle[]>>
}) {
	// Only fetch if we have contract
	if (!contract) {
		// Only clear vehicles if they're not already empty to prevent unnecessary state updates
		if (currentVehicles.length > 0) {
			setVehicles([])
		}
		return
	}

	try {
		const url = Endpoints.v2.uriVehicles(`contract/${contract}`)
		const fetchedVehicles = await API.get({ url })

		// Handle error response
		if (!fetchedVehicles.ok) {
			console.error("Failed to fetch vehicles:", fetchedVehicles.data)
			notification.error({
				message: "Error",
				description: "Failed to fetch vehicles.",
			})
		}

		setVehicles(fetchedVehicles.data?.results?.vehicles ?? [])
	} catch (error) {
		console.error("Error fetching vehicles:", error)
	}
}

async function fetchDrivers({
	contract,
	currentDrivers,
	setDrivers,
}: {
	contract?: string
	currentDrivers: any[]
	setDrivers: React.Dispatch<React.SetStateAction<any[]>>
}) {
	// Only fetch if we have contract
	if (!contract) {
		// Only clear drivers if they're not already empty to prevent unnecessary state updates
		if (currentDrivers.length > 0) {
			setDrivers([])
		}
		return
	}

	try {
		const url = Endpoints.v2.uriStaff(`contract/${contract}?role=driver`)
		const fetchedDrivers = await API.get({ url })

		// Handle error response
		if (!fetchedDrivers.ok) {
			console.error("Failed to fetch drivers:", fetchedDrivers.data)
			notification.error({
				message: "Error",
				description: "Failed to fetch drivers.",
			})
		}

		setDrivers(fetchedDrivers.data?.results?.staff ?? [])
	} catch (error) {
		console.error("Error fetching drivers:", error)
	}
}

async function fetchContractDetails({
	business,
	contract,
	setContractDetails,
}: {
	business?: string
	contract?: string
	setContractDetails: React.Dispatch<React.SetStateAction<any>>
}) {
	// Only fetch if we have contract
	if (!business || !contract) {
		setContractDetails(null)
		return
	}

	try {
		const url = Endpoints.uriBusinesses(`${business}/contracts/${contract}`)
		const response = await API.get({ url })

		// Handle error response
		if (!response.ok) {
			console.error("Failed to fetch contract details:", response.data)
			return
		}

		setContractDetails(response.data?.results?.contract ?? null)
	} catch (error) {
		console.error("Error fetching contract details:", error)
	}
}

async function fetchNotDeliveredReasons({
	business,
	setNotDeliveredReasons,
}: {
	business?: string
	setNotDeliveredReasons: React.Dispatch<
		React.SetStateAction<NotDeliveredReason[]>
	>
}) {
	// Only fetch if we have business
	if (!business) {
		setNotDeliveredReasons([])
		return
	}

	try {
		const response = await API.get({
			url: Endpoints.v2.uriSettings("not-delivered-reasons"),
			params: {
				business,
				onlyActive: true,
			},
		})

		if (response.ok && response.data && response.data.results) {
			setNotDeliveredReasons(response.data?.results?.notDeliveredReasons || [])
		}
	} catch (err) {
		console.error("Failed to fetch not delivered reasons:", err)
	}
}

export default Routes
